<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";

const COMPONENT_API = "https://api.aicoiot.com/componentapi.php";
const MICOAPP_API = "https://api.aicoiot.com/micoappapi.php";

/** 本机曾提交代码审核后，刷新页面仍走 getsubmitstatus，直至接口表现为无在审单（idle） */
function auditSubmitPendingStorageKey(appid) {
  return `micoapp_code_audit_use_submit_${appid || ""}`;
}

function formatApiJson(data) {
  try {
    return JSON.stringify(data, null, 2);
  } catch (e) {
    return String(data);
  }
}

/** 与微信 member_list 等返回兼容：members / list / member_list / data.* */
function extractTesterListFromResponse(d) {
  if (!d || typeof d !== "object") return [];
  if (Array.isArray(d.members)) return d.members;
  if (Array.isArray(d.list)) return d.list;
  if (Array.isArray(d.testers)) return d.testers;
  if (Array.isArray(d.member_list)) return d.member_list;
  if (Array.isArray(d.data)) return d.data;
  if (d.data && typeof d.data === "object") {
    const inner = d.data;
    if (Array.isArray(inner.members)) return inner.members;
    if (Array.isArray(inner.list)) return inner.list;
    if (Array.isArray(inner.member_list)) return inner.member_list;
  }
  return [];
}

function formatTesterCellText(row) {
  if (row == null) return "";
  if (typeof row === "string") return row;
  return (
    row.userstr ||
    row.username ||
    row.user_name ||
    row.weixin ||
    row.wechat_id ||
    (row.nickname && row.userstr ? `${row.nickname}（${row.userstr}）` : "") ||
    ""
  );
}

/** 与微信「最新审核单」常见 audit_status：0 成功 1 拒绝 2 审核中 3 撤回 */
function normalizeLatestAuditPayload(d) {
  if (!d || typeof d !== "object") return { phase: "idle", detail: "" };
  if (Number(d.errcode) !== 0) {
    return { phase: "error", detail: d.errmsg || "接口返回错误" };
  }
  let inner = d;
  if (d.audit_info && typeof d.audit_info === "object") {
    inner = d.audit_info;
  } else if (
    d.data &&
    typeof d.data === "object" &&
    !Array.isArray(d.data)
  ) {
    inner = d.data;
  }
  const ast =
    inner.audit_status !== undefined && inner.audit_status !== null
      ? inner.audit_status
      : inner.status;
  const reason =
    inner.reason ||
    inner.reason_for_audit ||
    d.reason ||
    "";
  if (ast === undefined || ast === null || ast === "") {
    return { phase: "idle", detail: "" };
  }
  if (typeof ast === "string") {
    if (ast.indexOf("审核中") !== -1) {
      return { phase: "reviewing", detail: ast };
    }
    if (ast.indexOf("撤回") !== -1) {
      return { phase: "withdrawn", detail: ast };
    }
    return { phase: "idle", detail: ast };
  }
  const n = Number(ast);
  if (n === 2) {
    return { phase: "reviewing", detail: reason || "审核中" };
  }
  if (n === 1) {
    return { phase: "rejected", detail: reason || "审核未通过" };
  }
  if (n === 0) {
    return { phase: "passed", detail: reason || "审核成功" };
  }
  if (n === 3) {
    return { phase: "withdrawn", detail: reason || "已撤回" };
  }
  return { phase: "idle", detail: String(ast) };
}

export default {
  page() {
    return {
      title: this.title,
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "审核管理",
      items: [],
      userinfo: {},
      appid: "",
      activeTab: "tester",
      testerWxId: "",
      testerLoading: false,
      testerList: [],
      testerListLoading: false,
      trialQrLoading: false,
      trialQrImgUrl: "",
      unbindLoadingUserstr: null,
      privacyRaw: "",
      privacyLoading: false,
      privacySetJson: "",
      privacySetLoading: false,
      lastAuditId: "",
      submitAuditLoading: false,
      latestAuditLoading: false,
      undoAuditLoading: false,
      latestAuditRaw: "",
      latestAuditPayload: null,
      micoBrief: null,
      micoBriefLoading: false,
      codePanelLoading: false,
    };
  },
  computed: {
    certIcpReady() {
      if (!this.micoBrief) return false;
      return (
        Number(this.micoBrief.realname) === 1 &&
        Number(this.micoBrief.icpstatus) === 1
      );
    },
    certIcpHintLines() {
      if (!this.micoBrief) return [];
      const lines = [];
      if (Number(this.micoBrief.realname) !== 1) {
        lines.push("微信认证须为「已认证」后方可提交代码审核");
      }
      if (Number(this.micoBrief.icpstatus) !== 1) {
        lines.push("小程序备案须为「已备案」后方可提交代码审核");
      }
      return lines;
    },
    mergedCodeAudit() {
      return normalizeLatestAuditPayload(this.latestAuditPayload);
    },
    canSubmitCodeAudit() {
      if (!this.certIcpReady || this.codePanelLoading) return false;
      return this.mergedCodeAudit.phase !== "reviewing";
    },
    canUndoCodeAudit() {
      if (!this.certIcpReady || this.codePanelLoading) return false;
      return this.mergedCodeAudit.phase === "reviewing";
    },
    codeAuditBanner() {
      if (this.codePanelLoading) {
        return {
          type: "info",
          title: "正在同步审核状态",
          description: "",
        };
      }
      if (!this.micoBrief) {
        return {
          type: "info",
          title: "正在加载小程序信息",
          description: "",
        };
      }
      if (!this.certIcpReady) {
        return {
          type: "error",
          title: "暂不可提交代码审核",
          description: this.certIcpHintLines.join("；"),
        };
      }
      const m = this.mergedCodeAudit;
      switch (m.phase) {
        case "reviewing":
          return {
            type: "warning",
            title: "代码审核进行中",
            description:
              "不可重复提交。「撤销代码审核」仅在微信侧允许撤回时可用。",
          };
        case "rejected":
          return {
            type: "warning",
            title: "审核未通过",
            description: m.detail || "请修改后重新提交代码审核。",
          };
        case "passed":
          return {
            type: "success",
            title: "审核已通过",
            description: "若需发布或再次提审，请按微信平台流程操作。",
          };
        case "error":
          return {
            type: "error",
            title: "获取审核状态异常",
            description: m.detail || "请稍后重试或点击查询按钮刷新。",
          };
        case "withdrawn":
          return {
            type: "info",
            title: "审核已撤回",
            description: "可重新提交代码审核。",
          };
        default:
          return {
            type: "success",
            title: "可提交代码审核",
            description: "当前未发现进行中的审核任务。",
          };
      }
    },
  },
  mounted() {
    try {
      this.userinfo =
        JSON.parse(localStorage.getItem("user") || "null") || {};
    } catch (e) {
      this.userinfo = {};
    }
    this.appid = this.$route.params.appid;
    this.setBreadcrumb();
    if (this.appid) {
      this.fetchTesterList();
    }
  },
  watch: {
    activeTab(val) {
      if (val === "code") {
        this.refreshCodeAuditPanel();
      }
      if (val === "tester") {
        this.fetchTesterList();
      }
    },
    "$route.params.appid"(newId) {
      if (newId && newId !== this.appid) {
        this.appid = newId;
        this.setBreadcrumb();
        this.privacyRaw = "";
        this.latestAuditRaw = "";
        this.latestAuditPayload = null;
        this.micoBrief = null;
        this.lastAuditId = "";
        this.testerList = [];
        this.trialQrImgUrl = "";
        if (this.activeTab === "code") {
          this.$nextTick(() => this.refreshCodeAuditPanel());
        }
        if (this.activeTab === "tester") {
          this.$nextTick(() => this.fetchTesterList());
        }
      }
    },
  },
  methods: {
    setBreadcrumb() {
      this.items = [
        { text: this.$t("menuitems.micoapp.text"), href: "/" },
        { text: this.$t("menuitems.icp.micoapp.list"), href: "/micoapp" },
        {
          text: this.$t("menuitems.icp.micoapp.info"),
          href: `/micoapp/${this.appid}`,
        },
        {
          text: "小程序管理",
          href: `/micoapp/${this.appid}#micoapp-wechat-mgmt`,
        },
        { text: "审核管理", active: true },
      ];
    },
    cmpHeaders() {
      return this.userinfo && this.userinfo.openid
        ? { Openid: this.userinfo.openid }
        : {};
    },
    /** 业务库中小程序记录含 auditid 即表示曾提交代码审核，应走 getsubmitstatus */
    hasMicoBriefAuditId() {
      const b = this.micoBrief;
      if (!b || b.auditid == null) return false;
      return String(b.auditid).trim() !== "";
    },
    shouldUseGetSubmitStatusForAudit() {
      if (this.lastAuditId && String(this.lastAuditId).trim() !== "") {
        return true;
      }
      if (this.hasMicoBriefAuditId()) {
        return true;
      }
      try {
        if (
          this.appid &&
          typeof sessionStorage !== "undefined" &&
          sessionStorage.getItem(auditSubmitPendingStorageKey(this.appid)) ===
            "1"
        ) {
          return true;
        }
      } catch (e) {
        /* ignore */
      }
      const m = normalizeLatestAuditPayload(this.latestAuditPayload);
      if (m.phase === "idle" || m.phase === "error") {
        return false;
      }
      return true;
    },
    setAuditSubmitPendingFlag() {
      try {
        if (this.appid && typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(
            auditSubmitPendingStorageKey(this.appid),
            "1"
          );
        }
      } catch (e) {
        /* ignore */
      }
    },
    clearAuditSubmitPendingFlag() {
      try {
        if (this.appid && typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem(auditSubmitPendingStorageKey(this.appid));
        }
      } catch (e) {
        /* ignore */
      }
    },
    applyLatestAuditResponse(data) {
      this.latestAuditPayload = data || null;
      this.latestAuditRaw = formatApiJson(this.latestAuditPayload);
      const m = normalizeLatestAuditPayload(this.latestAuditPayload);
      if (m.phase === "idle") {
        this.lastAuditId = "";
        this.clearAuditSubmitPendingFlag();
      }
    },
    async fetchMicoappBrief(options) {
      const silent = options && options.silent;
      if (!silent) this.micoBriefLoading = true;
      try {
        const res = await this.$axios.post(
          MICOAPP_API,
          {
            action: "micoapp.info",
            appid: this.appid,
          },
          { headers: this.cmpHeaders() }
        );
        const data = res.data && res.data.data;
        this.micoBrief =
          data && typeof data === "object" ? data : null;
        if (this.micoBrief && this.micoBrief.auditid != null) {
          const aid = String(this.micoBrief.auditid).trim();
          if (aid) {
            this.lastAuditId = aid;
          }
        }
      } catch (e) {
        console.error(e);
        this.micoBrief = null;
        if (!silent) {
          this.$message.error("加载小程序信息失败");
        }
      } finally {
        if (!silent) this.micoBriefLoading = false;
      }
    },
    async loadLatestAuditStatusData() {
      const action = this.shouldUseGetSubmitStatusForAudit()
        ? "getsubmitstatus"
        : "getLatestAuditStatus";
      const latRes = await this.$axios.post(
        COMPONENT_API,
        { action, appid: this.appid },
        { headers: this.cmpHeaders() }
      );
      this.applyLatestAuditResponse(latRes.data);
    },
    async refreshCodeAuditPanel() {
      if (!this.appid) return;
      this.codePanelLoading = true;
      try {
        await this.fetchMicoappBrief({ silent: true });
        await this.loadLatestAuditStatusData();
      } catch (e) {
        console.error(e);
        this.$message.error("同步审核状态失败");
      } finally {
        this.codePanelLoading = false;
      }
    },
    formatTesterCell(row) {
      const t = formatTesterCellText(row);
      return t || (typeof row === "object" && row ? JSON.stringify(row) : "—");
    },
    getTesterRowKey(row) {
      if (row && row.userstr) return row.userstr;
      return this.formatTesterCell(row);
    },
    async unsetTester(row) {
      const userstr =
        row && typeof row === "object" ? row.userstr || "" : "";
      if (!userstr) {
        this.$message.warning("缺少 userstr，无法解绑");
        return;
      }
      try {
        await this.$confirm("确定将该体验者从本小程序解绑吗？", "解绑体验者", {
          type: "warning",
        });
      } catch (e) {
        return;
      }
      this.unbindLoadingUserstr = userstr;
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          {
            action: "unset_tester",
            appid: this.appid,
            userstr,
          },
          { headers: this.cmpHeaders() }
        );
        const d = res.data || {};
        if (Number(d.errcode) === 0) {
          this.$message.success(d.errmsg || "已解绑");
          await this.fetchTesterList();
        } else {
          this.$message.error(d.errmsg || "解绑失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("解绑请求失败");
      } finally {
        this.unbindLoadingUserstr = null;
      }
    },
    async fetchTesterList() {
      if (!this.appid) return;
      this.testerListLoading = true;
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          {
            action: "get_tester",
            appid: this.appid,
          },
          { headers: this.cmpHeaders() }
        );
        const d = res.data || {};
        if (Number(d.errcode) !== 0) {
          this.testerList = [];
          this.$message.error(d.errmsg || "获取体验者列表失败");
          return;
        }
        this.testerList = extractTesterListFromResponse(d);
      } catch (e) {
        console.error(e);
        this.testerList = [];
        this.$message.error("获取体验者列表失败");
      } finally {
        this.testerListLoading = false;
      }
    },
    async fetchTrialQrcode() {
      if (!this.appid) return;
      this.trialQrLoading = true;
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          {
            action: "gettrialqrcode",
            appid: this.appid,
          },
          { headers: this.cmpHeaders() }
        );
        const d = res.data || {};
        const ec = d.errcode;
        if (ec !== undefined && ec !== null && Number(ec) !== 0) {
          this.$message.error(d.errmsg || "获取体验版二维码失败");
          return;
        }
        const img =
          d.img ||
          (d.data && typeof d.data === "object" && d.data.img) ||
          "";
        if (img) {
          this.trialQrImgUrl = img;
        } else {
          this.$message.warning("未返回二维码图片");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("获取体验版二维码失败");
      } finally {
        this.trialQrLoading = false;
      }
    },
    async setTester() {
      const username = (this.testerWxId || "").trim();
      if (!username) {
        this.$message.warning("请填写体验者微信号");
        return;
      }
      this.testerLoading = true;
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          {
            action: "set_tester",
            appid: this.appid,
            username,
          },
          { headers: this.cmpHeaders() }
        );
        const d = res.data || {};
        if (Number(d.errcode) === 0) {
          this.$message.success(d.errmsg || "已提交绑定体验者");
          this.testerWxId = "";
          await this.fetchTesterList();
        } else {
          this.$message.error(d.errmsg || "绑定失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("请求失败");
      } finally {
        this.testerLoading = false;
      }
    },
    async fetchPrivacy() {
      this.privacyLoading = true;
      this.privacyRaw = "";
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          {
            action: "getprivacysetting",
            appid: this.appid,
          },
          { headers: this.cmpHeaders() }
        );
        this.privacyRaw = formatApiJson(res.data || {});
        if (
          res.data &&
          Number(res.data.errcode) !== 0 &&
          res.data.errmsg
        ) {
          this.$message.warning(res.data.errmsg);
        }
      } catch (e) {
        console.error(e);
        this.$message.error("获取失败");
      } finally {
        this.privacyLoading = false;
      }
    },
    usePrivacyAsEditTemplate() {
      if (this.privacyRaw) {
        this.privacySetJson = this.privacyRaw;
      }
    },
    async savePrivacy() {
      const raw = (this.privacySetJson || "").trim();
      if (!raw) {
        this.$message.warning("请填写要提交的 JSON（可与微信开放平台接口字段一致）");
        return;
      }
      let extra;
      try {
        extra = JSON.parse(raw);
      } catch (e) {
        this.$message.error("JSON 格式不正确");
        return;
      }
      if (!extra || typeof extra !== "object" || Array.isArray(extra)) {
        this.$message.error("请提交 JSON 对象");
        return;
      }
      this.privacySetLoading = true;
      try {
        const clean = Object.assign({}, extra);
        delete clean.action;
        delete clean.appid;
        const payload = Object.assign(
          {
            action: "setprivacysetting",
            appid: this.appid,
          },
          clean
        );
        const res = await this.$axios.post(COMPONENT_API, payload, {
          headers: this.cmpHeaders(),
        });
        const d = res.data || {};
        if (Number(d.errcode) === 0) {
          this.$message.success(d.errmsg || "保存成功");
          await this.fetchPrivacy();
        } else {
          this.$message.error(d.errmsg || "保存失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("请求失败");
      } finally {
        this.privacySetLoading = false;
      }
    },
    async submitCodeAudit() {
      if (!this.certIcpReady) {
        this.$message.warning(this.certIcpHintLines.join("；"));
        return;
      }
      if (this.mergedCodeAudit.phase === "reviewing") {
        this.$message.warning("代码审核进行中，请勿重复提交");
        return;
      }
      this.submitAuditLoading = true;
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          {
            action: "submitmicoapp",
            appid: this.appid,
          },
          { headers: this.cmpHeaders() }
        );
        const d = res.data || {};
        if (Number(d.errcode) === 0) {
          this.setAuditSubmitPendingFlag();
          if (d.auditid != null && d.auditid !== "") {
            this.lastAuditId = String(d.auditid);
          }
          this.$message.success(
            d.auditid != null
              ? `已提交审核，审核单号：${d.auditid}`
              : d.errmsg || "已提交审核"
          );
          await this.refreshCodeAuditPanel();
        } else {
          this.$message.error(d.errmsg || "提交审核失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("请求失败");
      } finally {
        this.submitAuditLoading = false;
      }
    },
    async fetchLatestAuditStatus() {
      this.latestAuditLoading = true;
      try {
        if (!this.micoBrief && this.appid) {
          await this.fetchMicoappBrief({ silent: true });
        }
        const action = this.shouldUseGetSubmitStatusForAudit()
          ? "getsubmitstatus"
          : "getLatestAuditStatus";
        const res = await this.$axios.post(
          COMPONENT_API,
          {
            action,
            appid: this.appid,
          },
          { headers: this.cmpHeaders() }
        );
        this.applyLatestAuditResponse(res.data);
      } catch (e) {
        console.error(e);
        this.$message.error("查询失败");
      } finally {
        this.latestAuditLoading = false;
      }
    },
    async undoCodeAudit() {
      if (!this.canUndoCodeAudit) {
        this.$message.warning("当前状态不可撤销代码审核");
        return;
      }
      try {
        await this.$confirm(
          "确定撤销当前代码审核吗？（需符合微信侧条件）",
          "撤回代码审核",
          { type: "warning" }
        );
      } catch (e) {
        return;
      }
      this.undoAuditLoading = true;
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          {
            action: "undocodeaudit",
            appid: this.appid,
          },
          { headers: this.cmpHeaders() }
        );
        const d = res.data || {};
        if (Number(d.errcode) === 0) {
          this.$message.success(d.errmsg || "已请求撤回");
          await this.refreshCodeAuditPanel();
        } else {
          this.$message.error(d.errmsg || "撤回失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("请求失败");
      } finally {
        this.undoAuditLoading = false;
      }
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row micoapp-audit-page">
      <div class="col-12">
        <div class="card micoapp-info-card">
          <div class="card-body">
            <el-tabs v-model="activeTab">
              <el-tab-pane label="体验者" name="tester">
                <div v-loading="testerListLoading" class="micoapp-audit-tester-wrap">
                  <div class="row micoapp-audit-tester-layout">
                    <div class="col-lg-7">
                      <div class="d-flex flex-wrap align-items-center mb-3">
                        <el-button
                          class="mr-2 mb-2"
                          type="default"
                          plain
                          size="small"
                          :loading="testerListLoading"
                          @click="fetchTesterList"
                        >
                          刷新列表
                        </el-button>
                      </div>
                      <el-table
                        v-if="testerList.length"
                        :data="testerList"
                        :row-key="getTesterRowKey"
                        size="small"
                        border
                        stripe
                        class="mb-3 micoapp-audit-tester-table"
                      >
                        <el-table-column type="index" label="#" width="48" />
                        <el-table-column
                          label="userstr（微信侧标识）"
                          min-width="200"
                          show-overflow-tooltip
                        >
                          <template slot-scope="scope">
                            {{ formatTesterCell(scope.row) }}
                          </template>
                        </el-table-column>
                        <el-table-column
                          label="操作"
                          width="88"
                          align="center"
                          fixed="right"
                        >
                          <template slot-scope="scope">
                            <el-button
                              type="text"
                              class="p-0"
                              :loading="
                                unbindLoadingUserstr === scope.row.userstr
                              "
                              :disabled="
                                unbindLoadingUserstr === scope.row.userstr ||
                                !scope.row.userstr
                              "
                              @click="unsetTester(scope.row)"
                            >
                              解绑
                            </el-button>
                          </template>
                        </el-table-column>
                      </el-table>
                      <p
                        v-else-if="!testerListLoading"
                        class="text-muted small mb-3"
                      >
                        暂无体验者
                      </p>
                      <div class="micoapp-audit-tester-add">
                        <h6 class="micoapp-audit-section-title">添加体验者</h6>
                        <el-form
                          inline
                          class="micoapp-audit-tester-form"
                          @submit.native.prevent="setTester"
                        >
                          <el-form-item label="微信号">
                            <el-input
                              v-model="testerWxId"
                              placeholder="需绑定的微信号"
                              clearable
                              style="width: 240px"
                            />
                          </el-form-item>
                          <el-form-item>
                            <el-button
                              type="primary"
                              size="small"
                              :loading="testerLoading"
                              @click="setTester"
                            >
                              添加
                            </el-button>
                          </el-form-item>
                        </el-form>
                      </div>
                    </div>
                    <div class="col-lg-5 mt-4 mt-lg-0">
                      <div class="micoapp-audit-trial-panel">
                        <h6 class="micoapp-audit-section-title">体验版二维码</h6>
                        <p class="text-muted small mb-2">
                          体验者使用微信扫描可打开体验版小程序。
                        </p>
                        <el-button
                          type="primary"
                          plain
                          size="small"
                          :loading="trialQrLoading"
                          @click="fetchTrialQrcode"
                        >
                          获取体验版二维码
                        </el-button>
                        <div
                          v-if="trialQrImgUrl"
                          class="mt-3 micoapp-audit-trial-qr"
                        >
                          <img
                            :src="trialQrImgUrl"
                            alt="体验版二维码"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </el-tab-pane>
              <el-tab-pane label="用户隐私保护指引" name="privacy">
                <div class="mb-3">
                  <el-button
                    type="primary"
                    plain
                    size="small"
                    :loading="privacyLoading"
                    @click="fetchPrivacy"
                  >
                    获取当前配置
                  </el-button>
                  <el-button
                    type="default"
                    size="small"
                    :disabled="!privacyRaw"
                    @click="usePrivacyAsEditTemplate"
                  >
                    将结果填入编辑区
                  </el-button>
                </div>
                <div v-if="privacyRaw" class="mb-3">
                  <h6 class="micoapp-audit-section-title">接口返回</h6>
                  <pre class="micoapp-audit-pre">{{ privacyRaw }}</pre>
                </div>
                <h6 class="micoapp-audit-section-title">设置（JSON）</h6>
                <el-input
                  v-model="privacySetJson"
                  type="textarea"
                  :rows="14"
                  class="mb-2"
                />
                <el-button
                  type="primary"
                  :loading="privacySetLoading"
                  @click="savePrivacy"
                >
                  保存隐私保护指引
                </el-button>
              </el-tab-pane>
              <el-tab-pane label="代码审核" name="code">
                <div v-loading="codePanelLoading" class="micoapp-audit-code-wrap">
                  <el-alert
                    v-if="codeAuditBanner"
                    :title="codeAuditBanner.title"
                    :type="codeAuditBanner.type"
                    :closable="false"
                    show-icon
                    class="mb-3"
                  >
                    <p v-if="codeAuditBanner.description" class="mb-0 small">
                      {{ codeAuditBanner.description }}
                    </p>
                  </el-alert>
                  <div class="mb-3 d-flex flex-wrap align-items-center">
                  <el-button
                    class="mr-2 mb-2"
                    type="primary"
                    :loading="submitAuditLoading"
                    :disabled="!canSubmitCodeAudit || submitAuditLoading"
                    @click="submitCodeAudit"
                  >
                    提交代码审核
                  </el-button>
                  <el-button
                    class="mr-2 mb-2"
                    type="default"
                    plain
                    :loading="latestAuditLoading"
                    :disabled="codePanelLoading"
                    @click="fetchLatestAuditStatus"
                  >
                    查询最新审核单状态
                  </el-button>
                  <el-button
                    class="mb-2"
                    type="danger"
                    plain
                    :loading="undoAuditLoading"
                    :disabled="!canUndoCodeAudit || undoAuditLoading"
                    @click="undoCodeAudit"
                  >
                    撤销代码审核
                  </el-button>
                </div>
                <p v-if="lastAuditId" class="small mb-2">auditid：{{ lastAuditId }}</p>
                <div v-if="latestAuditRaw">
                  <h6 class="micoapp-audit-section-title">最新审核单</h6>
                  <pre class="micoapp-audit-pre">{{ latestAuditRaw }}</pre>
                </div>
                </div>
              </el-tab-pane>
            </el-tabs>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.micoapp-info-card {
  margin-bottom: 1.5rem;
}

.micoapp-audit-section-title {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
  margin: 0 0 0.5rem 0;
}

.micoapp-audit-pre {
  margin: 0;
  padding: 0.75rem 1rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8rem;
  max-height: 420px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.micoapp-audit-tester-layout {
  align-items: flex-start;
}

.micoapp-audit-tester-table {
  width: 100%;
}

.micoapp-audit-tester-add {
  padding: 1rem 1.25rem;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.micoapp-audit-tester-form {
  margin-bottom: 0;
}

.micoapp-audit-trial-panel {
  padding: 1rem 1.25rem;
  background: #fafbfc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  height: 100%;
}

@media (min-width: 992px) {
  .micoapp-audit-trial-panel {
    min-height: 280px;
  }
}

.micoapp-audit-trial-qr img {
  max-width: 240px;
  height: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}
</style>
